import React, { useContext, useEffect, useRef, useState } from "react";

import { BsMusicNoteList, BsFillCloudUploadFill } from "react-icons/bs";
import NavContext from "../../context/NavContext";
import AxiosDownload from "../../_services/download.service";
import { motion } from "framer-motion";
import { animatePage } from "../../animation/animationPage";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";
import DownloadBundleCard from "./DownloadBundleCard";

// 💡 Fonction utilitaire de téléchargement de fichier
const handleFileDownload = (response, defaultFilename = "file.zip") => {
  const contentDisposition = response.headers["content-disposition"];
  let filename = defaultFilename;

  if (contentDisposition) {
    const matches = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (matches?.[1]) filename = matches[1].replace(/['"]/g, "");
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DownloadOwner = () => {
  const { t } = useTranslation();
  const {
    ownerId,
    progressWord,
    setProgressWord,
    setProgressHearing,
    progressHearing,
  } = useContext(NavContext);

  const [mobile, setMobile] = useState(false);
  const warningToastIdRef = useRef(null);
  const warningClosedByUserRef = useRef(false);
  const wasDownloadInProgressRef = useRef(false);

  useEffect(() => {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
    ) {
      setMobile(true);
    }
  }, []);

  // 🧩 Fonction générique de téléchargement
  const downloadFile = (endpoint, setProgress, defaultFilename) => {
    setProgress(0);
    AxiosDownload.get(endpoint, {
      onDownloadProgress: (e) => {
        const percent = Math.round((e.loaded * 100) / (e.total || 1));
        setProgress(percent);
      },
    })
      .then((response) => {
        handleFileDownload(response, defaultFilename);
        toast.success(t("download.completed", { filename: defaultFilename }));
        setTimeout(() => setProgress(0), 2000);
      })
      .catch((error) => {
        console.error("Erreur de téléchargement :", error);
        toast.error(t("download.error_occurred"));
        setProgress(0);
      });
  };

  // Téléchargement spécifique
  const downloadHearing = () => {
    if (progressHearing > 0 && progressHearing < 100) return;
    downloadFile(
      `/dwHearing/${ownerId}`,
      setProgressHearing,
      "HearingSpaceSetup.zip",
    );
  };

  const downloadWords = () => {
    if (progressWord > 0 && progressWord < 100) return;
    downloadFile(
      `/dwWords/${ownerId}`,
      setProgressWord,
      "HearingSpaceFrenchWords.zip",
    );
  };

  const isAnyDownloadInProgress =
    (progressHearing > 0 && progressHearing < 100) ||
    (progressWord > 0 && progressWord < 100);

  useEffect(() => {
    if (isAnyDownloadInProgress && !wasDownloadInProgressRef.current) {
      warningClosedByUserRef.current = false;
    }

    if (isAnyDownloadInProgress && !warningClosedByUserRef.current) {
      if (!warningToastIdRef.current || !toast.isActive(warningToastIdRef.current)) {
        warningToastIdRef.current = toast.warning(t("download.warning_message"), {
          autoClose: false,
          closeOnClick: false,
          draggable: true,
          position: "top-right",
          onClose: () => {
            warningClosedByUserRef.current = true;
            warningToastIdRef.current = null;
          },
        });
      }
    }

    if (!isAnyDownloadInProgress && warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }

    wasDownloadInProgressRef.current = isAnyDownloadInProgress;
  }, [isAnyDownloadInProgress, t]);

  useEffect(() => {
    return () => {
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current);
      }
    };
  }, []);

  return (
    <motion.div
      variants={animatePage}
      initial="hidden"
      animate="visible"
      exit="exit"
    
    >

      <PermissionGate actionKey="download.HearingSpaceAndWords">
        <DownloadBundleCard
          onDownloadHearing={downloadHearing}
          onDownloadWords={downloadWords}
          progressHearing={progressHearing}
          progressWord={progressWord}
          canDownloadZips={true}
          isMobile={mobile}
          iconHearing={<BsFillCloudUploadFill />}
          iconWords={<BsMusicNoteList />}
          title={t("download.my_downloads")}
          //description="Hearing Space, la liste CNA et le manuel d'utilisation."
        />
      </PermissionGate>
    </motion.div>
  );
};

export default DownloadOwner;
